import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import Login from './pages/Login';
import Admin from './pages/Admin';
import History from './pages/History';
import { SavedQueries, SchemaExplorer, Settings } from './pages/OtherPages';
import CinematicSplash from './components/CinematicSplash';

// ── Splash screen ─────────────────────────────────────────────────────────

function SplashScreen({ onDone }) {
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setHiding(true), 1800);
    const t2 = setTimeout(() => onDone(), 2300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #020D05 0%, #051508 40%, #093515 70%, #020D05 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      animation: hiding ? 'splashFadeOut 0.5s ease forwards' : 'none',
      pointerEvents: hiding ? 'none' : 'all'
    }}>
      <div style={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(27,94,59,0.25) 0%, transparent 70%)',
        top: '5%', left: '5%', animation: 'orbFloat 9s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute', width: 280, height: 280, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(46,125,82,0.2) 0%, transparent 70%)',
        bottom: '10%', right: '10%', animation: 'orbFloat 12s ease-in-out infinite reverse'
      }} />

      {/* Ambient glow ring behind logo */}
      <div style={{
        position: 'absolute', width: 280, height: 280, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(27,94,59,0.18) 0%, transparent 70%)',
        top: '50%', left: '50%', transform: 'translate(-50%, -60%)', pointerEvents: 'none'
      }} />

      <div style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
        {/* Gear spins into view on every reload */}
        <img
          src="/logo.svg"
          alt="SolutionOne"
          style={{
            width: 168, height: 'auto', marginBottom: 16, display: 'block', margin: '0 auto 16px',
            filter: 'drop-shadow(0 0 28px rgba(27,94,59,0.72)) drop-shadow(0 8px 24px rgba(0,0,0,0.5))',
            animation: 'splashGearSpin 1s cubic-bezier(0.175,0.885,0.32,1.275) 0.15s both'
          }}
        />
        <div style={{ color: '#4CAF7D', fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', fontWeight: 600, marginBottom: 0 }}>
          ERP Intelligence Platform
        </div>
      </div>

      <style>{`
        @keyframes splashGearSpin {
          from { opacity:0; transform: scale(0.55); }
          to   { opacity:1; transform: scale(1); }
        }
      `}</style>

      <div style={{
        marginTop: 52, width: 180, height: 2, borderRadius: 2,
        background: 'rgba(255,255,255,0.1)', overflow: 'hidden',
        animation: 'fadeIn 0.3s 0.9s both'
      }}>
        <div style={{
          height: '100%', borderRadius: 2,
          background: 'linear-gradient(90deg, #1B5E3B, #2E7D52, #1B5E3B)',
          backgroundSize: '200% 100%',
          animation: 'gradientShimmer 1.2s ease infinite, fadeIn 0.3s 0.9s both',
          width: '100%'
        }} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 20, animation: 'fadeIn 0.3s 1s both' }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 7, height: 7, borderRadius: '50%', background: '#1B5E3B',
            animation: `bounce 1.4s ease-in-out ${i * 0.2}s infinite`
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Layout wrapper ────────────────────────────────────────────────────────

function Layout({ children }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);
  return (
    <div style={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{
        marginLeft: 240, flex: 1, minWidth: 0, minHeight: '100vh',
        background: '#EAF7F7', overflow: 'hidden',
        opacity: visible ? 1 : 0, transition: 'opacity 0.35s ease'
      }}>
        {children}
      </main>
    </div>
  );
}

// ── Route guards ──────────────────────────────────────────────────────────

function LoadingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 8 }}>
      {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B5E3B', animation: `bounce 1.4s ease-in-out ${i*0.2}s infinite` }} />)}
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingDots />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingDots />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login"   element={<Login />} />
      <Route path="/admin"   element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="/"        element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/saved"   element={<ProtectedRoute><SavedQueries /></ProtectedRoute>} />
      <Route path="/schema"  element={<ProtectedRoute><SchemaExplorer /></ProtectedRoute>} />
      <Route path="/settings"element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="*"        element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────

export default function App() {
  const [splashDone, setSplashDone] = useState(false);
  const [isFirstVisit] = useState(() => {
    const seen = sessionStorage.getItem('cs_seen');
    if (!seen) sessionStorage.setItem('cs_seen', '1');
    return !seen;
  });

  return (
    <AuthProvider>
      {!splashDone && (
        isFirstVisit
          ? <CinematicSplash onDone={() => setSplashDone(true)} />
          : <SplashScreen onDone={() => setSplashDone(true)} />
      )}
      <div style={{ opacity: splashDone ? 1 : 0, transition: 'opacity 0.4s ease' }}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}
